import { Fragment, type CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from './Icon';

// ════════════════════════════════════════════════════════════════════
// MONEY THREAD — il filo del denaro
// Ribbon compatto di chip-stadio che lega i documenti della stessa
// commessa (es. Preventivo → Progetto → Fattura → Incasso). Una riga,
// wrap automatico su schermi stretti (flex-wrap di .money-thread).
// Classi dal contratto CSS: .money-thread .mt-chip .mt-chip--ghost .mt-arrow
// ════════════════════════════════════════════════════════════════════

export type MoneyThreadState = 'done' | 'current' | 'future';

export interface MoneyThreadStep {
  /** Chiave stabile dello stadio (es. 'preventivo') */
  key: string;
  label: string;
  /** Dettaglio secondario: codice progetto, importo, scadenza attesa… */
  sub?: string;
  /** Rotta di destinazione: se presente la chip naviga al click */
  to?: string;
  state: MoneyThreadState;
}

export function MoneyThread({ steps }: { steps: MoneyThreadStep[] }) {
  const navigate = useNavigate();
  if (steps.length === 0) return null;

  return (
    <nav className="money-thread" aria-label="Filo del denaro">
      {steps.map((s, i) => {
        const clickable = !!s.to;
        // Stadio corrente: bordo accent pieno per evidenziare il "qui sei tu".
        // Chip senza rotta: niente affordance di click (cursor di default).
        const style: CSSProperties = {};
        if (s.state === 'current') style.borderColor = 'var(--accent)';
        if (!clickable) style.cursor = 'default';

        const chipCls = `mt-chip${s.state === 'future' ? ' mt-chip--ghost' : ''}`;
        const inner = (
          <>
            {s.state === 'done' && <Icon name="check" size={12} className="shrink-0" />}
            {s.state === 'future' && <EmptyCircle />}
            <span>{s.label}</span>
            {s.sub && <span style={{ fontWeight: 500, opacity: 0.75 }}>{s.sub}</span>}
          </>
        );

        return (
          <Fragment key={s.key}>
            {/* Separatore tra gli stadi */}
            {i > 0 && (
              <span className="mt-arrow" aria-hidden="true">
                <Icon name="chevronR" size={12} />
              </span>
            )}
            {clickable ? (
              <button type="button" className={chipCls} style={style} title={`Apri ${s.label.toLowerCase()}`} onClick={() => navigate(s.to!)}>
                {inner}
              </button>
            ) : (
              <span className={chipCls} style={style} aria-current={s.state === 'current' ? 'step' : undefined}>
                {inner}
              </span>
            )}
          </Fragment>
        );
      })}
    </nav>
  );
}

/** Cerchio vuoto: simbolo dello stadio futuro non ancora raggiunto */
function EmptyCircle() {
  return (
    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true" className="shrink-0">
      <circle cx="5" cy="5" r="4" stroke="currentColor" strokeWidth="1.4" />
    </svg>
  );
}
